import React, { useEffect, useRef } from 'react';

/**
 * GlobeBackground - 3D rotating wireframe globe of nodes and connections.
 * Rendered on a 2D canvas with manual spherical projection (no Three.js dependency).
 *
 * Variants control the colour palette:
 *   - "gold": champagne gold on obsidian (Milano luxury)
 *   - "red":  red on dark navy (original BOND brand)
 *   - "mix":  gold base + ruby red accent nodes (best of both)
 */
const PALETTES = {
  gold: {
    bg: '#050505',
    primary: 'rgba(212,175,55,',     // #D4AF37
    secondary: 'rgba(245,232,200,',  // warm cream
    accent: 'rgba(255,240,180,',     // bright gold highlight
    line: 'rgba(212,175,55,',
    accentSolid: '#D4AF37',
  },
  red: {
    bg: '#06111f',
    primary: 'rgba(255,255,255,',
    secondary: 'rgba(200,220,255,',
    accent: 'rgba(230,40,40,',       // bright red highlight
    line: 'rgba(180,205,255,',
    accentSolid: '#e60000',
  },
  mix: {
    bg: '#050505',
    primary: 'rgba(212,175,55,',
    secondary: 'rgba(245,232,200,',
    accent: 'rgba(220,30,60,',       // ruby red for hotspots
    line: 'rgba(212,175,55,',
    accentSolid: '#D4AF37',
  },
};

export default function GlobeBackground({ variant = 'gold', className = '', style = {} }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');
    const palette = PALETTES[variant] || PALETTES.gold;

    let W = 0, H = 0;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = container.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    // Generate evenly-distributed nodes on a sphere using Fibonacci lattice
    const N = 280;
    const HOTSPOTS = 10; // major cities / accent nodes
    const phi = Math.PI * (3 - Math.sqrt(5));
    const nodes = Array.from({ length: N }, (_, i) => {
      const y = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const theta = phi * i;
      return {
        x0: Math.cos(theta) * r,
        y0: y,
        z0: Math.sin(theta) * r,
        isHotspot: i < HOTSPOTS,
      };
    });

    const onMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      mouseRef.current.x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      mouseRef.current.y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      mouseRef.current.active = true;
    };
    const onMouseLeave = () => {
      mouseRef.current.active = false;
    };
    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mouseleave', onMouseLeave);

    let rafId;
    let t0 = performance.now();
    let autoRotY = 0;
    let autoRotX = 0;

    const frame = (t) => {
      const dt = Math.min((t - t0) / 1000, 0.05);
      t0 = t;

      // Auto-rotation with mouse influence
      autoRotY += dt * 0.18;
      if (mouseRef.current.active) {
        autoRotY += dt * mouseRef.current.x * 0.6;
        autoRotX += (mouseRef.current.y * 0.3 - autoRotX) * 0.05;
      } else {
        autoRotX += (Math.sin(t * 0.0003) * 0.15 - autoRotX) * 0.02;
      }

      const cy = Math.cos(autoRotY), sy = Math.sin(autoRotY);
      const cx = Math.cos(autoRotX), sx = Math.sin(autoRotX);

      const cxC = W / 2;
      const cyC = H / 2;
      const radius = Math.min(W, H) * 0.42;

      // Project each node
      const projected = nodes.map((n) => {
        // Rotate around Y axis
        let x = n.x0 * cy + n.z0 * sy;
        let z = -n.x0 * sy + n.z0 * cy;
        // Rotate around X axis
        let y = n.y0 * cx - z * sx;
        z = n.y0 * sx + z * cx;
        // Perspective projection
        const depth = z + 2;
        const scale = 1.6 / depth;
        const px = cxC + x * radius * scale;
        const py = cyC + y * radius * scale;
        const alpha = Math.max(0.05, (z + 1) / 2);
        return { px, py, z, alpha, isHotspot: n.isHotspot, scale };
      });

      ctx.clearRect(0, 0, W, H);

      // Subtle radial glow behind globe
      const grad = ctx.createRadialGradient(cxC, cyC, 0, cxC, cyC, radius * 1.4);
      grad.addColorStop(0, palette.line + '0.08)');
      grad.addColorStop(1, palette.line + '0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // Draw connecting lines (only nodes facing forward)
      const MD = radius * 0.35;
      for (let i = 0; i < projected.length; i++) {
        if (projected[i].z < -0.4) continue;
        for (let j = i + 1; j < projected.length; j++) {
          if (projected[j].z < -0.4) continue;
          const dx = projected[j].px - projected[i].px;
          const dy = projected[j].py - projected[i].py;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < MD) {
            const a = (1 - d / MD) * 0.32 * Math.min(projected[i].alpha, projected[j].alpha);
            ctx.strokeStyle = palette.line + a + ')';
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(projected[i].px, projected[i].py);
            ctx.lineTo(projected[j].px, projected[j].py);
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      for (const p of projected) {
        if (p.z < -0.6) continue;
        const r = p.isHotspot ? 3.2 : 1.5;
        if (p.isHotspot) {
          // Hotspot pulse rings
          const pulse = 0.5 + 0.5 * Math.sin(t * 0.002 + p.px * 0.01);
          ctx.beginPath();
          ctx.arc(p.px, p.py, r + pulse * 6, 0, Math.PI * 2);
          ctx.fillStyle = palette.accent + (0.08 * p.alpha) + ')';
          ctx.fill();
          ctx.beginPath();
          ctx.arc(p.px, p.py, r + pulse * 3, 0, Math.PI * 2);
          ctx.fillStyle = palette.accent + (0.2 * p.alpha) + ')';
          ctx.fill();
          ctx.beginPath();
          ctx.arc(p.px, p.py, r, 0, Math.PI * 2);
          ctx.fillStyle = palette.accent + (0.95 * p.alpha) + ')';
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.arc(p.px, p.py, r, 0, Math.PI * 2);
          ctx.fillStyle = palette.primary + (0.85 * p.alpha) + ')';
          ctx.fill();
        }
      }

      // Subtle equator/meridian outline (very faint)
      ctx.strokeStyle = palette.line + '0.08)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.ellipse(cxC, cyC, radius, radius * Math.abs(cx), 0, 0, Math.PI * 2);
      ctx.stroke();

      rafId = requestAnimationFrame(frame);
    };
    rafId = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [variant]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden ${className}`}
      style={{ background: PALETTES[variant]?.bg || '#050505', ...style }}
      data-testid={`globe-bg-${variant}`}
    >
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
}
